import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { WilayahCombobox } from "@/components/network/WilayahCombobox";
import { INFRA_STATUS, STATUS_LABEL, SELECT_CLS, apiError } from "@/lib/network";

const odcVal = (o) => o.id;
const odcLabel = (o) => `${o.kode} — ${o.nama}`;

export function PortFormDialog({ open, olt, port, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF(port ? { ...port, slot: port.slot ?? "", nomor_port: port.nomor_port ?? "", kapasitas_onu: port.kapasitas_onu ?? "" }
      : { nama_port: "", slot: "", nomor_port: "", kapasitas_onu: "", status: "AKTIF", catatan: "" });
  }, [open, port]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const n = (v) => (v === "" || v == null ? null : Number(v));
  const submit = async (e) => {
    e.preventDefault();
    const body = { nama_port: f.nama_port, slot: n(f.slot), nomor_port: n(f.nomor_port), kapasitas_onu: n(f.kapasitas_onu), status: f.status, catatan: f.catatan || "" };
    try {
      await (port ? api.put(`/network/pon-ports/${port.id}`, body) : api.post(`/network/olt/${olt.id}/ports`, body));
      toast.success(`Port ${f.nama_port} tersimpan`);
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="port-form-dialog">
        <DialogHeader><DialogTitle>{port ? `Edit Port ${port.nama_port}` : `Tambah Port PON — ${olt?.kode}`}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Nama port (sesuai perangkat, mis. 1/1/1 atau PON1)</Label>
            <Input value={f.nama_port || ""} onChange={set("nama_port")} required data-testid="port-form-nama" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Slot</Label><Input type="number" min="0" value={f.slot ?? ""} onChange={set("slot")} data-testid="port-form-slot" /></div>
            <div><Label>Nomor</Label><Input type="number" min="0" value={f.nomor_port ?? ""} onChange={set("nomor_port")} data-testid="port-form-nomor" /></div>
            <div><Label>Maks ONU</Label><Input type="number" min="1" value={f.kapasitas_onu ?? ""} onChange={set("kapasitas_onu")} data-testid="port-form-kapasitas" /></div>
          </div>
          <div><Label>Status</Label><select className={`${SELECT_CLS} w-full`} value={f.status || "AKTIF"} onChange={set("status")} data-testid="port-form-status">
            {INFRA_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</select></div>
          <div><Label>Catatan</Label><Input value={f.catatan || ""} onChange={set("catatan")} data-testid="port-form-catatan" /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Batal</Button><Button type="submit" data-testid="port-form-submit">Simpan</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BulkPortDialog({ open, olt, onClose, onSaved }) {
  const [f, setF] = useState({ slot: "1", mulai: "1", sampai: "8", awalan: "", kapasitas_onu: "" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const n = (v) => (v === "" ? null : Number(v));
  const preview = (i) => `${f.awalan}${f.slot === "" ? "" : `${f.slot}/`}${i}`;
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/network/olt/${olt.id}/ports/bulk`, { slot: n(f.slot), mulai: Number(f.mulai), sampai: Number(f.sampai), awalan: f.awalan, kapasitas_onu: n(f.kapasitas_onu) });
      toast.success(`${r.data.created.length} port dibuat${r.data.skipped.length ? `, ${r.data.skipped.length} sudah ada (dilewati)` : ""}`);
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-port-dialog">
        <DialogHeader><DialogTitle>Buat Port PON Sekaligus — {olt?.kode}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-[#6B7280]">Isi sesuai port yang benar-benar terpasang di perangkat. Kosongkan slot bila perangkat tidak memakai slot.</p>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Awalan (opsional)</Label><Input value={f.awalan} onChange={set("awalan")} placeholder="mis. gpon-olt_" data-testid="bulk-awalan" /></div>
            <div><Label>Slot</Label><Input type="number" min="0" value={f.slot} onChange={set("slot")} data-testid="bulk-slot" /></div>
            <div><Label>Port mulai</Label><Input type="number" min="0" value={f.mulai} onChange={set("mulai")} required data-testid="bulk-mulai" /></div>
            <div><Label>Port sampai</Label><Input type="number" min="0" value={f.sampai} onChange={set("sampai")} required data-testid="bulk-sampai" /></div>
            <div><Label>Maks ONU per port</Label><Input type="number" min="1" value={f.kapasitas_onu} onChange={set("kapasitas_onu")} data-testid="bulk-kapasitas" /></div>
          </div>
          <p className="rounded bg-[#F8F9FA] p-2 text-xs" data-testid="bulk-preview">Contoh nama: <b>{preview(f.mulai)}</b> … <b>{preview(f.sampai)}</b></p>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Batal</Button><Button type="submit" data-testid="bulk-submit">Buat Port</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ConnectDialog({ port, olt, onClose, onSaved }) {
  const [odcs, setOdcs] = useState([]);
  const [odcId, setOdcId] = useState("");
  const [catatan, setCatatan] = useState("");
  useEffect(() => {
    if (port) { setOdcId(""); setCatatan(""); api.get("/network/odc", { params: { status: "AKTIF" } }).then((r) => setOdcs(r.data)); }
  }, [port]);
  const submit = async () => {
    if (!odcId) return toast.error("Pilih ODC");
    try {
      await api.post("/network/pon-links", { pon_port_id: port.id, odc_id: odcId, catatan });
      toast.success("Port PON terhubung ke ODC");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={!!port} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="connect-dialog">
        <DialogHeader><DialogTitle>Hubungkan {olt?.kode} {port?.nama_port} ke ODC</DialogTitle></DialogHeader>
        <Label>ODC (hanya yang AKTIF)</Label>
        <WilayahCombobox className="w-full" options={odcs} value={odcId} onChange={setOdcId} testid="connect-odc" placeholder="— Pilih ODC —"
                         getValue={odcVal} getLabel={odcLabel} getSub={(o) => o.lokasi_nama || ""} searchPlaceholder="Cari kode/nama ODC…" emptyText="ODC tidak ditemukan." />
        <Label>Catatan</Label><Input value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="connect-catatan" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={submit} data-testid="connect-submit">Hubungkan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DisconnectDialog({ port, onClose, onSaved }) {
  const [alasan, setAlasan] = useState("");
  useEffect(() => setAlasan(""), [port]);
  const submit = async () => {
    try {
      await api.post(`/network/pon-links/${port.link.id}/disconnect`, { alasan });
      toast.success("Relasi diputus (riwayat tetap tersimpan)");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={!!port} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="disconnect-dialog">
        <DialogHeader><DialogTitle>Putus {port?.nama_port} → {port?.link?.odc?.kode}?</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6B7280]">Relasi diakhiri dan disimpan sebagai riwayat. Data ODC, ODP, dan pelanggan tidak berubah.</p>
        <Label>Alasan</Label><Input value={alasan} onChange={(e) => setAlasan(e.target.value)} placeholder="min. 3 karakter" data-testid="disconnect-alasan" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button>
          <Button variant="destructive" disabled={alasan.trim().length < 3} onClick={submit} data-testid="disconnect-submit">Putus Relasi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const spLabel = (s) => `${s.kode} — ${s.nama} (${s.rasio})`;

export function MigrateDialog({ port, olt, onClose, onSaved }) {
  const [sps, setSps] = useState([]);
  const [spId, setSpId] = useState("");
  const [free, setFree] = useState([]);
  const [out, setOut] = useState("");
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!port) return;
    setSpId(""); setFree([]); setOut(""); setAlasan("");
    api.get("/network/splitters", { params: { status: "AKTIF" } }).then((r) => setSps(r.data.filter((s) => !s.input))).catch(() => toast.error("Gagal memuat splitter"));
  }, [port]);
  useEffect(() => {
    setFree([]); setOut("");
    if (!spId) return;
    api.get(`/network/splitters/${spId}`).then((r) => {
      const used = new Set(r.data.outputs.map((o) => o.output_port));
      const f = Array.from({ length: r.data.jumlah_output }, (_, i) => i + 1).filter((n) => !used.has(n));
      setFree(f); setOut(f[0] ? String(f[0]) : "");
    }).catch(() => toast.error("Gagal memuat output splitter"));
  }, [spId]);
  const sp = sps.find((s) => s.id === spId);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/network/pon-links/${port.link.id}/migrate-to-splitter`, { splitter_id: spId, output_port: Number(out), alasan });
      toast.success("Relasi dipindahkan ke splitter (riwayat lama tersimpan)");
      if (r.data.warning) toast.warning(r.data.warning);
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const odc = port?.link?.odc?.kode;
  return (
    <Dialog open={!!port} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="migrate-dialog">
        <DialogHeader><DialogTitle>Pindahkan {port?.nama_port} → {odc} ke Splitter</DialogTitle></DialogHeader>
        {olt?.status === "PERBAIKAN" && <div className="rounded-md border border-[#FCD34D] bg-[#FEF3C7] px-3 py-2 text-xs font-semibold text-[#92400E]" role="alert" data-testid="migrate-olt-repair-warning">
          OLT sedang PERBAIKAN. Migrasi hanya merapikan jalur fisik yang sudah ada; layanan mungkin terganggu.</div>}
        <Label>Splitter tujuan (AKTIF, belum punya input)</Label>
        <WilayahCombobox className="w-full" options={sps} value={spId} onChange={setSpId} testid="migrate-splitter" placeholder="— Pilih splitter —"
                         getValue={odcVal} getLabel={spLabel} getSub={(s) => s.lokasi_fisik || ""} searchPlaceholder="Cari kode/nama splitter…" emptyText="Tidak ada splitter tersedia." />
        <Label>Output splitter ke {odc}</Label>
        <select className={`${SELECT_CLS} w-full`} value={out} onChange={(e) => setOut(e.target.value)} disabled={!spId} data-testid="migrate-output">
          {!free.length && <option value="">{spId ? "Semua output terpakai" : "Pilih splitter dulu"}</option>}
          {free.map((n) => <option key={n} value={n}>Output {n}</option>)}</select>
        <Label>Alasan</Label><Input value={alasan} onChange={(e) => setAlasan(e.target.value)} placeholder="min. 3 karakter" data-testid="migrate-alasan" />
        <div className="space-y-1 rounded bg-[#F8F9FA] p-2 text-xs" data-testid="migrate-summary">
          <div><span className="text-[#6B7280]">Sebelum:</span> <b>{olt?.kode} {port?.nama_port} → {odc}</b></div>
          <div><span className="text-[#6B7280]">Sesudah:</span> <b>{olt?.kode} {port?.nama_port} → {sp ? `${sp.kode} (${sp.rasio})` : "Splitter"} output {out || "?"} → {odc}</b></div>
          <div className="text-[#6B7280]">Relasi lama diakhiri dan tersimpan sebagai riwayat. ODP & pelanggan tidak berubah.</div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button>
          <Button disabled={busy || !spId || !out || alasan.trim().length < 3} onClick={submit} data-testid="migrate-submit">Pindahkan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
